'use memo';

import { useEffect, useMemo, useRef } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import { Text } from '@/components/Typography';
import type { ChatMessage } from '@/features/global-ai-chat/api/types';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { AiChatMessageBubble } from './AiChatMessageBubble';
type AiChatMessageListProps = {
  messages: ChatMessage[];
  /** 送信済みでアシスタント返信を待っている間、ローディングバブルを表示する。 */
  isAwaitingReply?: boolean;
  /** 送信失敗したユーザー発言（残してリトライさせる）。 */
  failedMessage?: {
    content: string;
  } | null;
  onRetry?: () => void;
};
function formatTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(iso));
}

/** チャットらしい 3 点バウンスのタイピングインジケータ。 */
function TypingIndicator({
  label
}: {
  label: string;
}) {
  return <div className="flex flex-col items-start gap-1">
      <div className={cn('flex max-w-[88%] items-center gap-1.5 rounded-2xl rounded-bl-none border px-3 py-3', 'border-neutral-muted-border bg-neutral-bottom-fill')} role="status" aria-label={label}>
        {[0, 1, 2].map(i => <span key={i} aria-hidden className={cn('size-1.5 animate-bounce rounded-full bg-neutral-muted-on-fill', i === 0 && '[animation-delay:-0.3s]', i === 1 && '[animation-delay:-0.15s]')} />)}
      </div>
    </div>;
}
export function AiChatMessageList({
  messages,
  isAwaitingReply = false,
  failedMessage = null,
  onRetry
}: AiChatMessageListProps) {
  const {
    t,
    i18n
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.globalAiChat'
  });
  const locale = i18n.language;
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const items = useMemo(() => messages.map(message => ({
    ...message,
    displayTime: formatTime(message.timestamp, locale)
  })), [messages, locale]);

  // 新着・ローディング・失敗表示の変化時に最下部へスクロールする。
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      block: 'end',
      behavior: 'smooth'
    });
  }, [items.length, isAwaitingReply, failedMessage]);
  return <section className="flex flex-col gap-3 px-4 py-4" role="log" aria-live="polite">
      {items.map(message => <AiChatMessageBubble key={message.messageId} role={message.role} content={message.content} timestamp={message.displayTime} citations={message.citations} usage={message.usage} canAnswer={message.canAnswer} />)}

      {isAwaitingReply ? <TypingIndicator label={t('assistantTypingAriaLabel')} /> : null}

      {failedMessage ? <div className="flex flex-col items-end gap-1">
          <div className={cn('max-w-[88%] rounded-2xl rounded-br-none border px-3 py-2', 'border-error-border bg-error-muted-fill')}>
            <Text variant="pSmall" className="whitespace-pre-wrap text-neutral-on-fill">
              {failedMessage.content}
            </Text>
          </div>
          <div className="flex items-center gap-2">
            <Text variant="labelExSmall" className="text-error-on-neutral-fill">
              {t('sendFailedLabel')}
            </Text>
            <ActionButton type="button" size="sm" variant="mutedGhost" icon="RefreshCwIcon" onClick={() => onRetry?.()}>
              {t('retryAction')}
            </ActionButton>
          </div>
        </div> : null}

      <div ref={bottomRef} />
    </section>;
}